import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from '../../../../environments/environment';
import { isClientSuspendedError, SKIP_AUTHZ_TOAST } from 'app/core/auth/auth.interceptor';
import { CompleteSetupBody, SeatLimitDto, SeatLimitWebApiResponse, SeatSummaryWebApiResponse, WorkspaceRecord } from './seat-summary.dto';
import { MembershipRespondResponse } from './membership-respond.dto';

// SPEC-meet-ux-004d §"API Contracts" / GAP-MEETUX-031C.
// Thin client for the seat-summary endpoint, which lives on the users-api
// host alongside the legacy Tenant/Workspace controllers. Created as part
// of 004d because the swagger-generated TenantService predates this route.
//
// Headers: Authorization Bearer is attached by AuthInterceptor; this service
// adds the x-tenant-id header explicitly to match the backend
// SeatSummaryBizQuery contract (workspaceId is also passed as a route param,
// so the header is just for consistency with the tenant-scoped pattern).
@Injectable({ providedIn: 'root' })
export class WorkspaceService {
    private readonly basePath = environment.api.users;
    private readonly subscriptionsPath = environment.api.subscriptions;

    // SPEC-web-planner-026 AC-1 (GAP-MEET-UX-314): one shared SeatLimit read per tenant id.
    private readonly seatLimitReads = new Map<string, Observable<SeatLimitDto | null>>();
    private invalidationWired = false;

    // AuthService is reached lazily (as persona.guard does): it is provided in AuthModule, so a
    // direct injection here would be a provider cycle.
    constructor(private http: HttpClient, private injector: Injector) {}

    /** Drops every kept SeatLimit read. */
    private clearSeatLimitReads(): void {
        this.seatLimitReads.clear();
    }

    /** On first use: a workspace switch or a sign-out empties the cache. */
    private wireInvalidation(): void {
        if (this.invalidationWired) { return; }
        const auth = this.injector.get(AuthService, null);
        if (!auth) { return; }
        this.invalidationWired = true;
        auth.selectedTenant$?.subscribe(() => this.clearSeatLimitReads());
        auth.signedOut$?.subscribe(() => this.clearSeatLimitReads());
    }

    getSeatSummary(workspaceId: string): Observable<SeatSummaryWebApiResponse> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        // Canonical route: GET /api/v1/Workspace/{workspaceId}/seat-summary on
        // Users.Api.Controllers.WorkspaceController (inherits ApplicationBaseController
        // → [Route("api/v{v:apiVersion}/[controller]")]). Previously omitted the
        // /api/v1/ prefix and 404'd — surfaced by SESSION-meet-ux-004e wizard
        // landing fresh admins on /admin and immediately calling this endpoint.
        return this.http.get<SeatSummaryWebApiResponse>(
            `${this.basePath}/api/v1/Workspace/${workspaceId}/seat-summary`,
            { headers }
        );
    }

    // SPEC-web-planner-013 AC-6: the client's plan and seat limit (10/25/50) from
    // subscriptions-api. Emits null when the call fails or is unsuccessful, so the
    // seat display can say "Seat limit unavailable" instead of guessing a number.
    // The caller handles 400/403/503 itself, so no authorization toast; a call
    // that hangs (cold start) gives up after 10 s rather than leave it pending.
    getSeatLimit(workspaceId: string): Observable<SeatLimitDto | null> {
        return this.seatLimitCall(workspaceId).pipe(catchError(() => of(null)));
    }

    // SPEC-web-planner-030 AC-10: the client's own record (GET api/v1/Workspace/{id}) for the Admin Home
    // workspace card: plan, onboarding date, website, currency, status. null when it cannot be read.
    getWorkspace(workspaceId: string): Observable<WorkspaceRecord | null> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        return this.http.get<{ isSuccess?: boolean; result?: WorkspaceRecord }>(
            `${this.subscriptionsPath}/api/v1/Workspace/${encodeURIComponent(workspaceId)}`,
            { headers, context }
        ).pipe(
            timeout(10_000),
            map(resp => (resp?.isSuccess && resp.result) ? resp.result : null),
            catchError(() => of(null)),
        );
    }

    // SPEC-web-planner-035 AC-3: PUT api/v1/Workspace (WorkspaceUpdateCommand { workspace }), Admin only. The backend maps the
    // whole body onto the stored record, so the caller sends the record as it read it (never a sparse body). The page shows
    // the server's message itself, so no authorization toast; errors reach the caller unchanged.
    updateWorkspace(workspaceId: string, record: WorkspaceRecord): Observable<{ isSuccess?: boolean; message?: string; result?: WorkspaceRecord }> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        return this.http.put<{ isSuccess?: boolean; message?: string; result?: WorkspaceRecord }>(
            `${this.subscriptionsPath}/api/v1/Workspace`,
            { workspace: record },
            { headers, context }
        );
    }

    // SPEC-web-planner-013 AC-24: just the client's status ("Active",
    // "PendingSetup", "Suspended"), or null when it cannot be read.
    // AC-26s: under Enforce a suspended client refuses SeatLimit itself with
    // 403 client_suspended, which is read as "Suspended".
    getClientStatus(workspaceId: string): Observable<string | null> {
        return this.seatLimitCall(workspaceId).pipe(
            map(limit => typeof limit?.clientStatus === 'string' ? limit.clientStatus : null),
            catchError(err => of(isClientSuspendedError(err) ? 'Suspended' : null)),
        );
    }

    // AC-1: shared per tenant id. A null or failed result is not kept, so the guard still
    // fails closed and the next call retries. An error (403 client_suspended) still reaches
    // the caller, and is not kept either.
    private seatLimitCall(workspaceId: string): Observable<SeatLimitDto | null> {
        this.wireInvalidation();
        const kept = this.seatLimitReads.get(workspaceId);
        if (kept) { return kept; }
        const forget = () => {
            if (this.seatLimitReads.get(workspaceId) === shared) { this.seatLimitReads.delete(workspaceId); }
        };
        const shared: Observable<SeatLimitDto | null> = this.seatLimitRequest(workspaceId).pipe(
            tap({ next: (v) => { if (!v) { forget(); } }, error: () => forget() }),
            shareReplay(1),
        );
        this.seatLimitReads.set(workspaceId, shared);
        return shared;
    }

    private seatLimitRequest(workspaceId: string): Observable<SeatLimitDto | null> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        return this.http.get<SeatLimitWebApiResponse>(
            `${this.subscriptionsPath}/api/v1/Workspace/SeatLimit`,
            { headers, context }
        ).pipe(
            timeout(10_000),
            map(resp => (resp?.isSuccess && resp.result) ? resp.result : null),
        );
    }

    // SPEC-web-planner-013 AC-14: the Admin of a PendingSetup client completes
    // setup. The setup form shows every answer itself (400/403/409/502), so no
    // authorization toast; errors reach the caller unchanged.
    completeSetup(workspaceId: string, body: CompleteSetupBody): Observable<unknown> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        // AC-1: PendingSetup -> Active must never be served from the kept read. Success empties the cache; an error (such as
        // 409 client_not_pending: the status changed, and the redirect that follows must read it afresh) drops this
        // workspace's entry only. Either runs before the result reaches the caller.
        return this.http.put(`${this.subscriptionsPath}/api/v1/Workspace/CompleteSetup`, body, { headers, context }).pipe(
            tap({ next: () => this.clearSeatLimitReads(), error: () => this.seatLimitReads.delete(workspaceId) }),
        );
    }

    // SPEC-web-planner-013 AC-50 (rev 13, R93): an Admin's edit of another
    // person (Users drawer) is checked against the client in x-tenant-id; the
    // generated UserService.userPut has no header parameter, so under Enforce
    // it was answered 400 workspace_required.
    updateUser(workspaceId: string, command: unknown): Observable<any> {
        const headers = new HttpHeaders({ 'x-tenant-id': workspaceId });
        // AC-54: the drawer shows the server's reason itself, so no generic toast
        // (a 403 client_suspended still opens the holding page, AC-26s).
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        // users-api UserController is [Route("[controller]")]: no api/v1 prefix (rev 15b).
        return this.http.put<any>(`${this.basePath}/User`, command, { headers, context });
    }

    // SPEC-web-planner-013 AC-55 (SPEC-009 AC-55): the signed-in person answers
    // their own invitation. users-api reads the client from the body, so no
    // x-tenant-id; the caller shows every failure itself, so no toast. No
    // client timeout: unsubscribing aborts the request, and users-api cancels
    // the answer on an aborted request, so a slow (cold-start) accept would
    // never be saved.
    respondToInvite(workspaceId: string, accept: boolean): Observable<MembershipRespondResponse> {
        const context = new HttpContext().set(SKIP_AUTHZ_TOAST, true);
        return this.http.put<MembershipRespondResponse>(
            `${this.basePath}/User/Membership/Respond`, // UserController: no api/v1 prefix
            { workspaceId, accept },
            { context }
        );
    }
}
